import React from "react";
import { PseaAnswer } from "./pseaScoring";

/**
 * Per-standard visual state, coloured by the card's own result rather than the
 * assessment's overall band: the two states that score a point read as success,
 * a standard the partner does not meet reads as danger, and unanswered stays
 * neutral so an incomplete assessment reads as incomplete rather than failed.
 *
 * "Not applicable" keeps the dashed border it has always had, so it stays
 * distinguishable from "Met" despite sharing the colour.
 */
export const standardState = (answer: PseaAnswer, standardNumber?: number) => {
  if (answer === "yes") {
    return {
      label: "Met",
      detail: "1 point",
      accent: "var(--vz-success)",
      cardClass: "bg-success-subtle",
      titleClass: "text-success",
      statusClass: "text-success",
      borderStyle: "solid" as const,
    };
  }
  if (answer === "na") {
    return {
      // Standard 8 is about corrective action for past SEA allegations, so
      // "Not applicable" reads as "No Allegations" there specifically.
      label: standardNumber === 8 ? "No Allegations" : "Not applicable",
      detail: "1 point",
      accent: "var(--vz-success)",
      cardClass: "bg-success-subtle",
      titleClass: "text-success",
      statusClass: "text-success",
      borderStyle: "dashed" as const,
    };
  }
  if (answer === "no") {
    return {
      label: "Not met",
      detail: "0 points",
      accent: "var(--vz-danger)",
      cardClass: "bg-danger-subtle",
      titleClass: "text-danger",
      statusClass: "text-danger",
      borderStyle: "solid" as const,
    };
  }
  // Unanswered is deliberately left as it was.
  return {
    label: "Not answered",
    detail: "0 points",
    accent: "var(--vz-gray-400)",
    cardClass: "bg-body-secondary",
    titleClass: "text-muted",
    statusClass: "text-muted",
    borderStyle: "dashed" as const,
  };
};

/** A single core-standard card; clicking it opens that standard. */
export const PSEAStandardCard = ({
  standard,
  answer,
  onSelect,
}: {
  standard: any;
  answer: PseaAnswer;
  onSelect: (standardNumber: number) => void;
}) => {
  const state = standardState(answer, standard.number);

  return (
    <button
      type="button"
      onClick={() => onSelect(standard.number)}
      title={standard.title}
      className={`text-start rounded p-2 ps-3 ${state.cardClass}`}
      style={{
        border: "none",
        borderLeft: `4px ${state.borderStyle} ${state.accent}`,
      }}
    >
      <div className="text-muted fs-11 font-monospace lh-1 mb-1" aria-hidden="true">
        {String(standard.number).padStart(2, "0")}
      </div>
      <div className={`fw-semibold fs-13 text-truncate ${state.titleClass}`}>
        {standard.title}
      </div>
      <div className={`fs-12 mt-1 ${state.statusClass}`}>{state.label}</div>
      <div className="text-muted fs-11">{state.detail}</div>
    </button>
  );
};

/** Shared grid wrapper so both headers lay the cards out identically. */
export const PSEAStandardGrid = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
      gap: "0.5rem",
    }}
  >
    {children}
  </div>
);
